import React, { useCallback, useEffect, useState } from 'react';
import axiosAdmin from '../AdminAxios.jsx';

/**
 * LES QUESTIONS DE LA SALLE, côté admin — onglet « Salle » de `/admin`.
 *
 * CE QU'IL RÉSOUT. La même conférence se donne plusieurs fois. Sans rangement,
 * la deuxième salle voit le nuage de mots de la première, la synthèse compte
 * des questions posées ailleurs, et le prompteur affiche une liste qui mélange
 * deux publics. Purger réglerait le problème — mais les questions d'une salle
 * sont exactement ce qu'on veut relire après coup.
 *
 * D'où deux gestes, et ils n'ont pas le même poids :
 *  - **Archiver** : la conférence passée sort du périmètre. Plus rien ne la
 *    lit (compteur, slide, synthèse, liste du prompteur), tout reste en base,
 *    et on la relit ici quand on veut. C'est le geste normal, entre deux
 *    conférences.
 *  - **Purger** : destruction définitive. Ne sert qu'à tenir la promesse faite
 *    en bas du formulaire — « les réponses sont supprimées après le
 *    séminaire » — une fois qu'on n'a plus besoin de rien.
 *
 * ⚠️ Ici, et seulement ici, `AdminAxios` est le bon outil : sur la page admin,
 * une session expirée DOIT renvoyer vers l'écran de connexion. C'est l'inverse
 * du prompteur et du diaporama, où cette redirection serait projetée.
 */

const Bouton = ({ ton = 'neutre', className = '', ...props }) => {
    const tons = {
        neutre: 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
        principal: 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700',
        danger: 'bg-white border-red-300 text-red-700 hover:bg-red-50',
    };
    return (
        <button
            {...props}
            className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tons[ton]} ${className}`}
        />
    );
};

const SalleAdmin = () => {
    const [sessions, setSessions] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [occupe, setOccupe] = useState(null);      // session en cours de traitement
    const [archives, setArchives] = useState(null);  // { session, lignes }

    const charger = useCallback(async () => {
        try {
            const { data } = await axiosAdmin.get('/api/salle/sessions');
            setSessions(data);
            setErreur(null);
        } catch (e) {
            setErreur(e?.response?.data?.error || 'lecture impossible');
        }
    }, []);

    useEffect(() => { charger(); }, [charger]);

    const archiver = async (s) => {
        if (!window.confirm(
            `Archiver « ${s.session} » ?\n\n`
            + `${s.actives} réponse(s) et ${s.syntheses_actives} synthèse(s) sortiront du périmètre : `
            + `le compteur, la slide et le prompteur repartiront de zéro.\n\n`
            + `Rien n'est détruit — tout reste consultable ici.`)) return;
        setOccupe(s.session);
        try {
            await axiosAdmin.post('/api/salle/archiver', { session: s.session });
            await charger();
        } catch (e) {
            setErreur(e?.response?.data?.error || 'archivage impossible');
        } finally {
            setOccupe(null);
        }
    };

    const purger = async (s) => {
        // Deux confirmations, et la seconde demande d'écrire le nom : c'est la
        // seule action de cette page qui détruit quelque chose pour de bon.
        if (!window.confirm(
            `SUPPRIMER DÉFINITIVEMENT « ${s.session} » ?\n\n`
            + `${s.total} réponse(s), archives comprises. Cette action est irréversible.\n\n`
            + `Pour simplement repartir à zéro, utilisez « Archiver ».`)) return;
        const saisi = window.prompt(`Écrivez le nom de la session pour confirmer : ${s.session}`);
        if (saisi !== s.session) return;
        setOccupe(s.session);
        try {
            await axiosAdmin.delete(`/api/salle/reponses?session=${encodeURIComponent(s.session)}`);
            await charger();
        } catch (e) {
            setErreur(e?.response?.data?.error || 'suppression impossible');
        } finally {
            setOccupe(null);
        }
    };

    const voirArchives = async (s) => {
        setOccupe(s.session);
        try {
            const { data } = await axiosAdmin.get(
                `/api/salle/reponses?session=${encodeURIComponent(s.session)}&archives=1`);
            setArchives({ session: s.session, lignes: data });
        } catch (e) {
            setErreur(e?.response?.data?.error || 'lecture des archives impossible');
        } finally {
            setOccupe(null);
        }
    };

    if (erreur && !sessions) {
        return <p className="text-red-600">Questions de la salle : {erreur}</p>;
    }
    if (!sessions) {
        return <p className="text-gray-400">Lecture…</p>;
    }

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Questions de la salle</h3>
                        <p className="text-sm text-gray-500">
                            Archiver entre deux conférences : la salle précédente sort du périmètre
                            sans être perdue.
                        </p>
                    </div>
                    <Bouton onClick={charger} className="ml-auto">⟲ Actualiser</Bouton>
                </div>

                {sessions.length === 0 ? (
                    <p className="px-6 py-8 text-gray-500">Aucune réponse enregistrée.</p>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <th className="px-6 py-3">Session</th>
                                <th className="px-6 py-3">En cours</th>
                                <th className="px-6 py-3">Archivées</th>
                                <th className="px-6 py-3">Dernière réponse</th>
                                <th className="px-6 py-3">Dernier archivage</th>
                                <th className="px-6 py-3" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {sessions.map((s) => (
                                <tr key={s.session} className="text-sm">
                                    <td className="px-6 py-4">
                                        <span className="font-semibold text-gray-900">{s.session}</span>
                                        {!s.ouverte && (
                                            <span className="ml-2 text-xs text-gray-400">(formulaire fermé)</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 tabular-nums">
                                        <span className={s.actives ? 'font-semibold text-gray-900' : 'text-gray-400'}>
                                            {s.actives}
                                        </span>
                                        {s.syntheses_actives > 0 && (
                                            <span className="ml-2 text-xs text-emerald-700">
                                                + {s.syntheses_actives} synthèse{s.syntheses_actives > 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 tabular-nums text-gray-500">{s.archivees}</td>
                                    <td className="px-6 py-4 text-gray-500">{s.derniere || '—'}</td>
                                    <td className="px-6 py-4 text-gray-500">{s.dernier_archivage || '—'}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex justify-end gap-2">
                                            <Bouton
                                                ton="principal"
                                                disabled={occupe === s.session || s.actives === 0}
                                                onClick={() => archiver(s)}
                                                title={s.actives === 0
                                                    ? 'Rien à archiver : cette session est déjà rangée'
                                                    : 'Sortir ces réponses du périmètre, sans les détruire'}
                                            >
                                                Archiver
                                            </Bouton>
                                            <Bouton
                                                disabled={occupe === s.session || s.archivees === 0}
                                                onClick={() => voirArchives(s)}
                                            >
                                                Archives ({s.archivees})
                                            </Bouton>
                                            <Bouton
                                                ton="danger"
                                                disabled={occupe === s.session || s.total === 0}
                                                onClick={() => purger(s)}
                                                title="Suppression définitive — pour tenir la promesse faite au formulaire"
                                            >
                                                Purger
                                            </Bouton>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {erreur && <p className="px-6 py-3 text-sm text-red-600">{erreur}</p>}
            </div>

            {archives && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Archives de « {archives.session} »
                            <span className="ml-2 text-sm font-normal text-gray-400 tabular-nums">
                                {archives.lignes.length} réponse{archives.lignes.length > 1 ? 's' : ''}
                            </span>
                        </h3>
                        <Bouton className="ml-auto" onClick={() => setArchives(null)}>Fermer</Bouton>
                    </div>
                    <div className="max-h-[28rem] overflow-y-auto divide-y divide-gray-100">
                        {archives.lignes.map((l, i) => (
                            <div key={i} className="px-6 py-3 text-sm">
                                <div className="flex gap-3 text-xs text-gray-400 tabular-nums">
                                    <span>{l.jour} {l.heure}</span>
                                    {l.frequence && <span>· {l.frequence}</span>}
                                    {l.mot && <span>· « {l.mot} »</span>}
                                    <span className="ml-auto">archivée le {l.archivee}</span>
                                </div>
                                {l.question && <p className="mt-1 text-gray-800">{l.question}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalleAdmin;
